import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex flex-col items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              {t('no_content')}
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              {t('back_to_home')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter(
    (a) => article.related.includes(a.id)
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('nav.articles'), path: '/' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 min-w-0">
              <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {t('article.published')}: {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {t('article.author')}: {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} {t('article.views')}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-secondary-100 text-secondary-700 text-xs px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedArticles.length > 0 && (
                <div className="mt-14 pt-8 border-t border-background-200">
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">
                    {t('article.related')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="flex gap-4 bg-background-50 border border-background-200 rounded-xl p-4 cursor-pointer hover:border-background-300 transition-all duration-300 group"
                      >
                        <div className="w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-1">
                            {related.date}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.image,
              datePublished: article.date,
              author: { '@type': 'Person', name: article.author },
              publisher: {
                '@type': 'Organization',
                name: '蜜桃影视',
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://entervue.github.io/article/${article.id}`,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}